import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { TourStep, getTourSteps } from "./tour-steps";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { usePathway } from "@/contexts/PathwayContext";
import { useUserSession } from "@/hooks/use-user-session";
import { SpotlightOverlay } from "./SpotlightOverlay";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";

interface SpotlightTourProps {
  onActiveChange?: (active: boolean) => void;
}

export function SpotlightTour({ onActiveChange }: SpotlightTourProps) {
  const { userRole, userProfile } = useRole();
  const { data: session } = useUserSession();
  const currentUserId = session?.userId ?? null;
  const queryClient = useQueryClient();
  const { setCurrentPathway } = usePathway();
  const { showCenterPicker } = useActiveLocation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [checked, setChecked] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Notify parent of active state
  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  // Don't start tour while on tutor profile page or center picker is open
  const isTutorOnProfile = userRole === "tutor" && location.pathname === "/tutor-profile";
  const shouldBlockTour = showCenterPicker || isTutorOnProfile;

  // Check if user needs tour — uses cached tourCompleted from the shared session
  useEffect(() => {
    if (!userProfile || checked || !currentUserId || shouldBlockTour) return;
    if (session?.tourCompleted === null || session?.tourCompleted === undefined) return;

    setChecked(true);
    if (!session.tourCompleted) {
      setTimeout(() => {
        const tourSteps = getTourSteps(userRole);
        setSteps(tourSteps);
        setIsActive(true);
      }, 1200);
    }
  }, [userProfile, userRole, checked, currentUserId, shouldBlockTour, session?.tourCompleted]);

  const completeTour = useCallback(async () => {
    setIsActive(false);
    if (!currentUserId) return;

    await supabase
      .from("profiles")
      .update({ tour_completed: true })
      .eq("id", currentUserId);

    queryClient.invalidateQueries({ queryKey: ["user-session"] });

    // Also mark pages visited during the tour
    const pageKeys = steps
      .filter((s) => s.pageKey)
      .map((s) => s.pageKey as string);

    if (pageKeys.length > 0) {
      const rows = pageKeys.map((pk) => ({
        user_id: currentUserId,
        page_key: pk,
      }));
      // Insert all page tour completions (ignore conflicts)
      for (const row of rows) {
        await supabase.from("user_page_tours").insert(row).select();
      }
    }
  }, [steps, currentUserId]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const nextStep = steps[currentStep + 1];

      // Handle pathway switch
      if (nextStep.switchPathway) {
        setIsNavigating(true);
        setCurrentPathway(nextStep.switchPathway);
        if (nextStep.navigateTo) {
          navigate(nextStep.navigateTo);
        }
        // Wait for pathway switch and navigation to settle
        setTimeout(() => {
          setCurrentStep((s) => s + 1);
          setIsNavigating(false);
        }, 1000);
        return;
      }

      // Handle navigation
      if (nextStep.navigateTo) {
        setIsNavigating(true);
        navigate(nextStep.navigateTo);
        setTimeout(() => {
          setCurrentStep((s) => s + 1);
          setIsNavigating(false);
        }, 600);
        return;
      }

      setCurrentStep((s) => s + 1);
    } else {
      completeTour();
    }
  }, [currentStep, steps, navigate, setCurrentPathway, completeTour]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = steps[currentStep - 1];

      // If going back requires different pathway or navigation
      if (prevStep.switchPathway || prevStep.navigateTo) {
        setIsNavigating(true);
        if (prevStep.switchPathway) {
          setCurrentPathway(prevStep.switchPathway);
        }
        // For going back, figure out the right pathway from the step
        const targetPathway = prevStep.navigateTo?.startsWith("/assessment")
          ? "assessment"
          : prevStep.navigateTo?.startsWith("/learning")
          ? "learning"
          : null;
        if (targetPathway) {
          setCurrentPathway(targetPathway);
        }
        if (prevStep.navigateTo) {
          navigate(prevStep.navigateTo);
        }
        setTimeout(() => {
          setCurrentStep((s) => s - 1);
          setIsNavigating(false);
        }, 600);
        return;
      }

      setCurrentStep((s) => s - 1);
    }
  }, [currentStep, steps, navigate, setCurrentPathway]);

  if (!isActive || steps.length === 0) return null;

  return (
    <SpotlightOverlay
      steps={steps}
      currentStep={currentStep}
      onNext={handleNext}
      onPrev={handlePrev}
      onClose={completeTour}
      isNavigating={isNavigating}
    />
  );
}
